// A-Structure — Market structure from OHLCV candles. Zero AI. Zero guessing.
// ═══════════════════════════════════════════════════════════════════════════
// This is a direct port of the VALIDATED Pine Script logic
// (JarvisSMC_Strategy.pine — see jarvisDNA.js → trading.validated_strategy).
// Backtested: XAUUSD 15m/45m/1h/4h + BTCUSD 30m/1h. BTCUSD 4h EXCLUDED
// (failed 0/11 then 0/6 across two separate backtests, even with ADX filter).
//
// ⚠️ This REPLACES the old 1-candle-wiggle pivot detector. That old version
// flagged almost every local high/low as a "swing" — far too noisy to match
// what was actually backtested. The fix: a `swingLen`-bar CONFIRMED pivot,
// with the leg direction alternating by construction (a new high pivot can
// never register twice in a row without a low pivot forming in between) —
// this is what keeps swingHigh/swingLow a naturally PAIRED, adjacent set and
// avoids the "stale range" bug the very first (manual-anchor) Jarvis script
// had on the real XAUUSD chart.
// ═══════════════════════════════════════════════════════════════════════════

const BULLISH_LEG = 1;
const BEARISH_LEG = 0;

// Highest high / lowest low over `len` candles ENDING at `endIdx` (inclusive)
// — mirrors Pine's ta.highest(len)/ta.lowest(len), which is a trailing window
// ending at the current bar, not a centred window.
function highestHigh(candles, endIdx, len) {
  let h = -Infinity;
  for (let i = Math.max(0, endIdx - len + 1); i <= endIdx; i++) {
    if (candles[i].high > h) h = candles[i].high;
  }
  return h;
}
function lowestLow(candles, endIdx, len) {
  let l = Infinity;
  for (let i = Math.max(0, endIdx - len + 1); i <= endIdx; i++) {
    if (candles[i].low < l) l = candles[i].low;
  }
  return l;
}

// candles: [{ open, high, low, close, date }, ...] oldest → newest
// swingLen: confirmed-pivot bars each side. Validated default = 50.
export async function run({ candles = [], swingLen = 50 }) {
  if (!candles || candles.length < swingLen + 2) {
    return {
      ok: false,
      error: `Need at least ${swingLen + 2} candles for structure analysis (got ${candles?.length || 0})`,
      agent: 'A-Structure',
      requiresAI: false,
      ts: Date.now(),
    };
  }

  const c = candles;

  let legDir      = 0;      // current confirmed leg direction (persists as we walk forward)
  let swingHigh   = null;   // { price, index, date }
  let swingLow    = null;
  let bias        = 0;      // 0 = undetermined, 1 = BULLISH, -1 = BEARISH
  let highCrossed = true;   // has the current swingHigh already triggered its BOS/CHoCH?
  let lowCrossed  = true;

  // Walk forward bar-by-bar exactly like the Pine script does (not a
  // vectorised/batch shortcut) — this keeps the behaviour identical to what
  // was actually backtested, including which specific bar each pivot and
  // each BOS/CHoCH lands on.
  for (let i = swingLen; i < c.length; i++) {
    const pivotIdx = i - swingLen;   // the candle that MIGHT just be confirmed as a pivot

    const newLegHigh = c[pivotIdx].high > highestHigh(c, i, swingLen);
    const newLegLow  = c[pivotIdx].low  < lowestLow(c, i, swingLen);

    let newLeg = legDir;
    if (newLegHigh) newLeg = BEARISH_LEG;       // a top just got confirmed
    else if (newLegLow) newLeg = BULLISH_LEG;   // a bottom just got confirmed

    if (newLeg !== legDir) {
      if (newLeg === BULLISH_LEG) {
        swingLow   = { price: c[pivotIdx].low, index: pivotIdx, date: c[pivotIdx].date };
        lowCrossed = false;
      } else {
        swingHigh   = { price: c[pivotIdx].high, index: pivotIdx, date: c[pivotIdx].date };
        highCrossed = false;
      }
      legDir = newLeg;
    }

    // BOS / CHoCH — confirmed on CANDLE CLOSE crossing the level, never a
    // wick. This matches ta.crossover(close, ...)/ta.crossunder(close, ...)
    // in the Pine script — wick-based confirmation lets fakeouts/liquidity
    // sweeps flip the bias on noise.
    if (swingHigh && !highCrossed && c[i].close > swingHigh.price) {
      highCrossed = true;
      bias = 1;   // BULLISH
    }
    if (swingLow && !lowCrossed && c[i].close < swingLow.price) {
      lowCrossed = true;
      bias = -1;  // BEARISH
    }
  }

  if (!swingHigh || !swingLow || bias === 0) {
    return {
      ok: true,
      valid: false,
      trend: 'UNDETERMINED',
      bias: 0,
      recommendation: 'Swing range अजून confirm झालेला नाही, किंवा अजून एकही BOS/CHoCH झाला नाही — wait करा, trade नाही.',
      agent: 'A-Structure',
      requiresAI: false,
      ts: Date.now(),
    };
  }

  const isBullOriented = bias === 1;

  // Trend-adaptive orientation — SAME convention as TradingView's own Fib
  // Retracement tool: bullish → 0 = swing LOW (origin), 1 = swing HIGH
  // (extreme). Bearish → flipped. NEVER fixed to "0=high" like the very
  // first Jarvis Pine script (that was M4 in BUGS_AND_GAPS.md — hardcoded
  // direction regardless of trend).
  const fibOrigin  = isBullOriented ? swingLow.price  : swingHigh.price;
  const fibExtreme = isBullOriented ? swingHigh.price : swingLow.price;

  return {
    ok:    true,
    valid: true,
    trend: isBullOriented ? 'BULLISH' : 'BEARISH',
    bias,                       // 1 | -1
    swingHigh,
    swingLow,
    fibOrigin,
    fibExtreme,
    currentPrice: c[c.length - 1].close,
    candleCount:  c.length,
    swingLenUsed: swingLen,
    recommendation: isBullOriented
      ? 'Bullish structure (BOS confirmed). OTE zone (0.618-0.79 retracement) मध्ये price यायची वाट बघा.'
      : 'Bearish structure (BOS confirmed). OTE zone (0.618-0.79 retracement) मध्ये price यायची वाट बघा.',
    agent:      'A-Structure',
    requiresAI: false,
    ts:         Date.now(),
  };
}
